import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SsoIntegration } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import Button from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Table from '@lib/components/Table/Table';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import Trans from '@lib/components/Trans/Trans';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import { IconName } from '@lib/components/Modal/enums';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import getTableActionsMenu from './utils/getTableActionsMenu';
import getTableColumns from './utils/getTableColumns';
import useActions from './utils/useActions';

const modals = [Modals.SsoIntegrationDestroyModal];

export default function SsoIntegrations() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const useModalManagementResult = useModalManagement(modals);
  const { closeModal, isModalOpened } = useModalManagementResult;
  const {
    ssoIntegrationsQueryHookResult,
    onConfirmedDelete,
    onActivate,
    onCreate,
    onDeactivate,
    onDelete,
    onEdit,
    onView,
  } = useActions({
    useModalManagementResult,
  });

  const tableActionsMenu = getTableActionsMenu({
    t,
    onActivate,
    onDeactivate,
    onEdit,
    onView,
    onDelete,
  });
  const tableColumns = getTableColumns({ t });

  const tableEmptyState = (
    <ListEmptyState
      icon={IconName.VerifiedUser}
      title={t('no-sso-connections-yet')}
      text={<Trans i18nKey="no-sso-connections-text" />}
      actions={[
        <Button
          key="add"
          buttonText={t('add-sso-connection')}
          leftIcon="add"
          onClick={() => onCreate()}
        />,
      ]}
    />
  );

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      <PageBody withoutHeader>
        <WhiteBox>
          <DashboardTitleHeader
            actions={[
              <Button
                key="add"
                buttonText={t('add-sso-connection')}
                data-test-id="sso__add-button"
                leftIcon="add"
                onClick={() => onCreate()}
              />,
            ]}
            title={t('single-sign-on-sso')}
          />
          {ssoIntegrationsQueryHookResult.firstLoading ? (
            <CircleLoader />
          ) : (
            <Table<SsoIntegration>
              actionsMenu={tableActionsMenu}
              columns={tableColumns}
              data={ssoIntegrationsQueryHookResult.collection}
              emptyState={tableEmptyState}
              id="sso-integrations"
              loading={ssoIntegrationsQueryHookResult.loading}
              totalEntries={ssoIntegrationsQueryHookResult.totalCount}
            />
          )}
        </WhiteBox>
      </PageBody>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-sso-connection-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.SsoIntegrationDestroyModal)}
        onClose={() => closeModal(Modals.SsoIntegrationDestroyModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
    </>
  );
}
